import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { bridgeService } from '@/services/bridge';
import { fetchPendingSendsV2, type PendingSendsV2Response } from '@/services/self-relay';
import type {
  BridgeFees,
  BridgeInfo,
  PendingBatch,
  PendingSendsResponse,
  PendingDepositsResponse,
} from '@/types/bridge';

// Query keys for cache management
const bridgeKeys = {
  all: ['bridge'] as const,
  fees: (amount?: string) => [...bridgeKeys.all, 'fees', amount] as const,
  pendingSends: (address: string) => [...bridgeKeys.all, 'pending-sends', address] as const,
  pendingSendsV2: (address: string) => [...bridgeKeys.all, 'pending-sends-v2', address] as const,
  pendingDeposits: (address: string) => [...bridgeKeys.all, 'pending-deposits', address] as const,
  pendingBatches: () => [...bridgeKeys.all, 'pending-batches'] as const,
  info: () => [...bridgeKeys.all, 'info'] as const,
};

/**
 * Hook to fetch bridge fees for a given amount
 * @param amountUgraviton Amount in ugraviton to calculate fees for
 */
export function useBridgeFees(amountUgraviton?: string): UseQueryResult<BridgeFees, Error> {
  return useQuery<BridgeFees, Error>({
    queryKey: bridgeKeys.fees(amountUgraviton),
    queryFn: () => bridgeService.getBridgeFees(amountUgraviton),
    staleTime: 30_000, // 30 seconds - fees can change with ETH gas prices
    enabled: true, // Always fetch to get default fees
  });
}

/**
 * Hook to fetch pending sends for a Gravity address
 * @param address Gravity Bridge address
 */
export function usePendingSends(
  address: string | undefined
): UseQueryResult<PendingSendsResponse, Error> {
  return useQuery<PendingSendsResponse, Error>({
    queryKey: bridgeKeys.pendingSends(address ?? ''),
    queryFn: () => bridgeService.getPendingSends(address!),
    enabled: address !== undefined && address.startsWith('gravity1'),
    staleTime: 10_000, // 10 seconds
    refetchInterval: 30_000, // Auto-refresh every 30 seconds
  });
}

/**
 * Hook to fetch pending sends v2 (with batch info) for a Gravity address
 * @param address Gravity Bridge address
 */
export function usePendingSendsV2(
  address: string | undefined
): UseQueryResult<PendingSendsV2Response, Error> {
  return useQuery<PendingSendsV2Response, Error>({
    queryKey: bridgeKeys.pendingSendsV2(address ?? ''),
    queryFn: () => fetchPendingSendsV2(address!),
    enabled: address !== undefined && address.startsWith('gravity1'),
    staleTime: 10_000, // 10 seconds
    refetchInterval: 15_000, // Auto-refresh every 15 seconds for relay UI
  });
}

/**
 * Hook to fetch pending deposits for a Gravity address (ETH → Gravity)
 * @param address Gravity Bridge address
 */
export function usePendingDeposits(
  address: string | undefined
): UseQueryResult<PendingDepositsResponse, Error> {
  return useQuery<PendingDepositsResponse, Error>({
    queryKey: bridgeKeys.pendingDeposits(address ?? ''),
    queryFn: () => bridgeService.getPendingDeposits(address!),
    enabled: address !== undefined && address.startsWith('gravity1'),
    staleTime: 10_000, // 10 seconds - deposits confirm on ~12s block time
    refetchInterval: 15_000, // Auto-refresh every 15 seconds for faster updates
  });
}

/**
 * Hook to fetch global pending batches
 */
export function usePendingBatches(): UseQueryResult<PendingBatch[], Error> {
  return useQuery<PendingBatch[], Error>({
    queryKey: bridgeKeys.pendingBatches(),
    queryFn: () => bridgeService.getPendingBatches(),
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to fetch bridge info
 */
export function useBridgeInfo(): UseQueryResult<BridgeInfo, Error> {
  return useQuery<BridgeInfo, Error>({
    queryKey: bridgeKeys.info(),
    queryFn: () => bridgeService.getBridgeInfo(),
    staleTime: Infinity, // Bridge info rarely changes
  });
}

/**
 * Hook to invalidate bridge-related caches
 */
export function useInvalidateBridge(): {
  invalidateAll: () => Promise<void>;
  invalidatePendingSends: (address: string) => Promise<void>;
  invalidatePendingSendsV2: (address: string) => Promise<void>;
  invalidatePendingDeposits: (address: string) => Promise<void>;
  invalidateFees: () => Promise<void>;
  invalidatePendingBatches: () => Promise<void>;
} {
  const queryClient = useQueryClient();

  return {
    /** Invalidate all bridge data */
    invalidateAll: (): Promise<void> => queryClient.invalidateQueries({ queryKey: bridgeKeys.all }),

    /** Invalidate pending sends for an address */
    invalidatePendingSends: (address: string): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: bridgeKeys.pendingSends(address) }),

    /** Invalidate pending sends v2 for an address */
    invalidatePendingSendsV2: (address: string): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: bridgeKeys.pendingSendsV2(address) }),

    /** Invalidate pending deposits for an address */
    invalidatePendingDeposits: (address: string): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: bridgeKeys.pendingDeposits(address) }),

    /** Invalidate bridge fees */
    invalidateFees: (): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: ['bridge', 'fees'] }),

    /** Invalidate pending batches */
    invalidatePendingBatches: (): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: bridgeKeys.pendingBatches() }),
  };
}

// Re-export types for consumers
export type { BridgeFees, BridgeInfo, PendingBatch, PendingSendsResponse, PendingDepositsResponse };
